// Focus management for the layered dialogs (form overlay, picker sheet,
// discard confirm) — task 5.1.
//
// `useFocusTrap` is a port of the prototype's hook (app.jsx): Tab and
// Shift-Tab wrap within the node, initial focus is handed to the first
// focusable after a tick (yielding to an autofocused descendant that already
// took it, e.g. the picker's search row), and focus returns to the
// previously-focused control when the layer deactivates — which is how the
// overlay restores focus to the pinned card's OPEN control and the picker to
// its invoking select, with no per-dialog restore logic.
//
// `useEscLayer` makes Esc precedence deterministic. The previous mechanism —
// the picker consuming Esc in capture phase so the overlay's bubble listener
// never fired — extended poorly to a third layer, so it becomes an explicit
// stack: each open layer registers, one capture-phase window listener closes
// only the topmost (last-registered) layer per press, and stops the event so
// nothing beneath sees it. Mount order is layer order: overlay, then the
// discard confirm or picker above it.

import { MutableRefObject, useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
    'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])';

/** The tick the trap waits before claiming initial focus (prototype metric). */
export const INITIAL_FOCUS_DELAY_MS = 40;

export const useFocusTrap = <T extends HTMLElement>(active: boolean = true) => {
    const ref = useRef<T>(null);

    useEffect(() => {
        const node = ref.current;
        if (!active || !node) return;
        const prev = document.activeElement as HTMLElement | null;

        // The prototype filters focusables by `offsetParent !== null`
        // (visibility). jsdom has no layout — every offsetParent is null and
        // the filter would empty the list — so probe the node itself and only
        // apply the visibility filter where layout actually exists.
        const focusables = (): HTMLElement[] => {
            const hasLayout = node.offsetParent !== null || node.offsetHeight > 0;
            return Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
                (el) =>
                    !(el as HTMLElement & { disabled?: boolean }).disabled &&
                    (!hasLayout || el.offsetParent !== null)
            );
        };

        const timer = window.setTimeout(() => {
            if (!node.contains(document.activeElement)) {
                (focusables()[0] ?? node).focus({ preventScroll: true });
            }
        }, INITIAL_FOCUS_DELAY_MS);

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;
            const items = focusables();
            if (!items.length) return;
            const index = items.indexOf(document.activeElement as HTMLElement);
            if (e.shiftKey && index <= 0) {
                e.preventDefault();
                e.stopPropagation(); // a wrap is this layer's — an outer trap must not also act
                items[items.length - 1].focus({ preventScroll: true });
            } else if (!e.shiftKey && index === items.length - 1) {
                e.preventDefault();
                e.stopPropagation();
                items[0].focus({ preventScroll: true });
            }
        };
        node.addEventListener('keydown', onKeyDown);

        return () => {
            window.clearTimeout(timer);
            node.removeEventListener('keydown', onKeyDown);
            if (prev && document.contains(prev)) prev.focus({ preventScroll: true });
        };
    }, [active]);

    return ref;
};

type EscHandler = () => void;

/** The open layers, bottom to top. Module-level: the widget mounts one forms
 *  surface, and Esc precedence is global by nature. */
const escStack: Array<MutableRefObject<EscHandler>> = [];

const onWindowKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || !escStack.length) return;
    e.preventDefault();
    e.stopPropagation();
    escStack[escStack.length - 1].current();
};

/** Register `onEsc` as the layer's close action while `active`. One Esc press
 *  closes exactly one layer — the topmost. */
export const useEscLayer = (onEsc: EscHandler, active: boolean = true): void => {
    const handlerRef = useRef(onEsc);
    handlerRef.current = onEsc;

    useEffect(() => {
        if (!active) return;
        escStack.push(handlerRef);
        if (escStack.length === 1) {
            window.addEventListener('keydown', onWindowKeyDown, true);
        }
        return () => {
            const index = escStack.indexOf(handlerRef);
            if (index >= 0) escStack.splice(index, 1);
            if (!escStack.length) {
                window.removeEventListener('keydown', onWindowKeyDown, true);
            }
        };
    }, [active]);
};
